import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import {
  ChevronLeftIcon,
  CloseIcon,
  SettingsIcon,
  SparkIcon,
  StaffIcon,
} from '@/components/Icons';
import { StatMetric } from '@/components/StatMetric';
import { Modal } from '@/components/Modal';
import { PhoneNumberField } from '@/components/PhoneNumberField';
import { PasswordInput } from '@/components/PasswordInput';
import { useAttendanceState } from '@/context/AttendanceContext';
import { useSessionState } from '@/context/SessionContext';
import { useStaffDirectoryState } from '@/context/StaffContext';
import { formatInrCurrency, getInitials } from '@/lib/format';
import { tintStyle } from '@/lib/style';
import {
  DEFAULT_PHONE_COUNTRY_CODE,
  joinPhoneNumber,
  splitPhoneNumber,
  type PhoneCountryCode,
} from '@/lib/phone';
import {
  createStaffFormSchema,
  updateStaffFormSchema,
  flattenFieldErrors,
  getFieldErrorsFromApiError,
} from '@/lib/validation';
import type { StaffMember } from '@/lib/types';

type StaffFormState = {
  name: string;
  email: string;
  phoneNumber: string;
  phoneCountryCode: PhoneCountryCode;
  password: string;
};

function StaffCard({
  staff,
  active,
  onEdit,
  onOpenProfile,
  onDelete,
}: {
  staff: StaffMember;
  active: boolean;
  onEdit: () => void;
  onOpenProfile: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="staff-card">
      <button className="staff-card-main" type="button" onClick={onOpenProfile}>
        <div className="avatar tinted" style={tintStyle(staff.color)}>
          {getInitials(staff.name)}
        </div>
        <div className="staff-card-copy">
          <div className="staff-card-title-row">
            <strong>{staff.name}</strong>
            <span className={`badge soft${active ? ' live' : ''}`}>{active ? 'On shift' : staff.role}</span>
          </div>
          <span>@{staff.username}</span>
          <span>{staff.email}</span>
          <p>
            {staff.phoneNumber}
            {staff.bookings > 0 || staff.revenue > 0
              ? ` · ${staff.bookings} bookings · ${formatInrCurrency(staff.revenue)}`
              : ''}
          </p>
        </div>
      </button>

      <div className="icon-action-row staff-card-actions">
        <button className="icon-button" type="button" title="Edit" aria-label={`Edit ${staff.name}`} onClick={onEdit}>
          <SettingsIcon size={16} />
        </button>
        <button
          className="icon-button"
          type="button"
          title="Delete"
          aria-label={`Delete ${staff.name}`}
          onClick={onDelete}>
          <CloseIcon size={16} />
        </button>
      </div>
    </div>
  );
}

export function StaffPage() {
  const navigate = useNavigate();
  const { sessionRole, isEnterprise, isReady: sessionReady } = useSessionState();
  const { isReady: attendanceReady, sessions } = useAttendanceState();
  const { isReady, staffMembers, addStaff, updateStaff, deleteStaff } = useStaffDirectoryState();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [form, setForm] = useState<StaffFormState>({
    name: '',
    email: '',
    phoneNumber: '',
    phoneCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
    password: '',
  });
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const activeStaffIds = useMemo(
    () => new Set(sessions.filter((session) => session.clockOutAt === null).map((session) => session.staffId)),
    [sessions]
  );

  const totalBookings = staffMembers.reduce((sum, staff) => sum + staff.bookings, 0);
  const totalRevenue = staffMembers.reduce((sum, staff) => sum + staff.revenue, 0);

  const visibleStaff = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return staffMembers;
    }

    return staffMembers.filter((staff) => {
      return (
        staff.name.toLowerCase().includes(query) ||
        staff.username.toLowerCase().includes(query) ||
        staff.email.toLowerCase().includes(query) ||
        staff.phoneNumber.toLowerCase().includes(query) ||
        staff.role.toLowerCase().includes(query)
      );
    });
  }, [searchQuery, staffMembers]);

  const deleteTarget = useMemo(
    () => staffMembers.find((staff) => staff.id === deleteTargetId) ?? null,
    [deleteTargetId, staffMembers]
  );

  function openAddForm() {
    setEditingId(null);
    setForm({
      name: '',
      email: '',
      phoneNumber: '',
      phoneCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
      password: '',
    });
    setFeedback(null);
    setFieldErrors({});
    setIsFormOpen(true);
  }

  function openEditForm(staff: StaffMember) {
    const parsedPhone = splitPhoneNumber(staff.phoneNumber);

    setEditingId(staff.id);
    setForm({
      name: staff.name,
      email: staff.email,
      phoneNumber: parsedPhone.phoneNumber,
      phoneCountryCode: parsedPhone.countryCode,
      password: '',
    });
    setFeedback(null);
    setFieldErrors({});
    setIsFormOpen(true);
  }

  async function handleSave() {
    try {
      const schema = editingId ? updateStaffFormSchema : createStaffFormSchema;
      const validation = schema.safeParse(form);

      if (!validation.success) {
        setFieldErrors(flattenFieldErrors(validation.error.flatten().fieldErrors));
        setFeedback(null);
        return;
      }

      const payload = validation.data;
      setFieldErrors({});

      const request = {
        name: payload.name,
        email: payload.email,
        phoneNumber: joinPhoneNumber(form.phoneCountryCode, payload.phoneNumber),
        password: payload.password,
      };

      if (editingId) {
        const result = await updateStaff(editingId, request);
        setFeedback({ kind: 'success', message: `${result?.name ?? 'Staff member'} updated.` });
      } else {
        const result = await addStaff(request);
        setFeedback({ kind: 'success', message: `${result.name} added.` });
      }

      setIsFormOpen(false);
      setEditingId(null);
      setForm({
        name: '',
        email: '',
        phoneNumber: '',
        phoneCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
        password: '',
      });
    } catch (saveError) {
      const apiFieldErrors = getFieldErrorsFromApiError(saveError);

      if (Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors((current) => ({ ...current, ...apiFieldErrors }));
        setFeedback(null);
        return;
      }

      if (
        saveError instanceof Error &&
        /already exists|username already exists|email already exists|phone number already exists/i.test(
          saveError.message
        )
      ) {
        setFieldErrors({ email: saveError.message });
        setFeedback(null);
        return;
      }

      setFeedback({
        kind: 'error',
        message:
          saveError instanceof Error && saveError.message ? saveError.message : 'Could not save the staff record.',
      });
    }
  }

  async function handleDelete() {
    if (!deleteTargetId) {
      return;
    }

    try {
      const removed = await deleteStaff(deleteTargetId);
      if (removed) {
        setFeedback({ kind: 'success', message: 'Staff member deleted.' });
      }
    } catch (error) {
      setFeedback({
        kind: 'error',
        message: error instanceof Error && error.message ? error.message : 'Could not delete the staff record.',
      });
    } finally {
      setDeleteTargetId(null);
    }
  }

  if (!sessionReady || !attendanceReady) {
    return <div className="section">Loading staff...</div>;
  }

  if (sessionRole === null) {
    return <Navigate to="/login" replace />;
  }

  if (!isEnterprise) {
    return <Navigate to="/settings" replace />;
  }

  if (!isReady) {
    return <div className="section">Loading staff...</div>;
  }

  return (
    <div className="section page-grid staff-page">
      {feedback ? <div className={`notice ${feedback.kind}`}>{feedback.message}</div> : null}

      <section className="hero-layout">
        <div className="hero-copy">
          <span className="eyebrow">
            <StaffIcon size={14} />
            <strong>Staff</strong>
            <span>{staffMembers.length} people</span>
          </span>

          <h1 className="display-title">
            <span>Team</span>
            <span>directory</span>
          </h1>

          <div className="button-row hero-actions">
            <Link className="ghost-button" to="/content-management">
              <ChevronLeftIcon size={16} />
              Content
            </Link>
            <button className="primary-button" type="button" onClick={openAddForm}>
              <SparkIcon size={16} />
              Add staff
            </button>
          </div>
        </div>

        <div className="stats-grid">
          <StatMetric label="Staff" value={String(staffMembers.length)} hint="Directory" />
          <StatMetric label="On shift" value={String(activeStaffIds.size)} hint="Live now" highlight />
          <StatMetric label="Bookings" value={String(totalBookings)} hint="All staff" />
          <StatMetric label="Revenue" value={formatInrCurrency(totalRevenue)} hint="Team total" />
        </div>
      </section>

      <section className="panel">
        <div className="panel-title-row">
          <div>
            <h2 className="panel-title">Directory</h2>
            <p className="panel-caption">Open profile · edit · delete</p>
          </div>
          <label className="field staff-search-field">
            <span className="visually-hidden">Search staff</span>
            <input
              className="input"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search name, username, email, phone"
            />
          </label>
        </div>

        <div className="staff-list">
          {visibleStaff.length > 0 ? (
            visibleStaff.map((staff) => (
              <StaffCard
                key={staff.id}
                staff={staff}
                active={activeStaffIds.has(staff.id)}
                onEdit={() => openEditForm(staff)}
                onOpenProfile={() => navigate(`/staff/${staff.id}`)}
                onDelete={() => setDeleteTargetId(staff.id)}
              />
            ))
          ) : (
            <div className="empty-state">
              {searchQuery.trim() ? 'No staff match that search.' : 'No staff yet. Add the first team member.'}
            </div>
          )}
        </div>
      </section>

      <Modal
        open={isFormOpen}
        title={editingId ? 'Edit staff' : 'Add staff'}
        description={editingId ? 'Update contact details or password.' : 'Create a new team login.'}
        onClose={() => setIsFormOpen(false)}>
        <div className="field-grid form-stack">
          <label className="field">
            <span className="field-label">Name</span>
            <input
              className="input"
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              placeholder="Staff name"
            />
            {fieldErrors.name ? <span className="field-error">{fieldErrors.name}</span> : null}
          </label>

          <div className="split-grid">
            <label className="field">
              <span className="field-label">Email</span>
              <input
                className="input"
                value={form.email}
                onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                placeholder="name@salon.com"
              />
              {fieldErrors.email ? <span className="field-error">{fieldErrors.email}</span> : null}
            </label>

            <label className="field">
              <span className="field-label">{editingId ? 'New password' : 'Password'}</span>
              <PasswordInput
                className="input"
                value={form.password}
                onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
                placeholder={editingId ? 'Leave blank to keep' : 'Set password'}
                autoComplete="new-password"
              />
              {fieldErrors.password ? <span className="field-error">{fieldErrors.password}</span> : null}
            </label>
          </div>

          <PhoneNumberField
            countryCode={form.phoneCountryCode}
            phoneNumber={form.phoneNumber}
            onChangeCountryCode={(nextValue) =>
              setForm((current) => ({ ...current, phoneCountryCode: nextValue }))
            }
            onChangePhoneNumber={(nextValue) => setForm((current) => ({ ...current, phoneNumber: nextValue }))}
            errorText={fieldErrors.phoneNumber}
            placeholder="9876543210"
          />

          <div className="button-row">
            <button className="primary-button" type="button" onClick={handleSave}>
              <SparkIcon size={16} />
              {editingId ? 'Save changes' : 'Add staff'}
            </button>
            <button className="ghost-button" type="button" onClick={() => setIsFormOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={deleteTargetId !== null}
        title="Delete staff"
        description={deleteTarget ? `Remove ${deleteTarget.name} from the directory?` : 'Remove this staff record?'}
        onClose={() => setDeleteTargetId(null)}>
        <div className="button-row">
          <button className="primary-button" type="button" onClick={handleDelete}>
            Delete
          </button>
          <button className="ghost-button" type="button" onClick={() => setDeleteTargetId(null)}>
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
}
